/**
 * Rewrite a remote image URL to request a smaller variant when the
 * upstream CDN supports it. Currently handles Jetpack Photon hosts
 * (i*.wp.com), Pexels, and actnow4planetearth.com via our /api/public/img
 * proxy (caches to Storage with 1y immutable TTL).
 *
 * Pass `opts.ar` (aspect ratio = width / height) to request the upstream
 * image pre-cropped to that ratio. This avoids Lighthouse's
 * "image-aspect-ratio" warning when the displayed box (e.g. `aspect-square`
 * or `aspect-[3/2]`) doesn't match the upstream's natural ratio.
 *
 * Falls back to returning the URL unchanged when we don't recognize the host.
 */
export type ResizeOpts = { ar?: number };

export function resizeImg(
  url: string | undefined | null,
  width: number,
  optsOrQuality?: ResizeOpts | number,
): string {
  if (!url) return "";
  const opts: ResizeOpts =
    typeof optsOrQuality === "object" && optsOrQuality !== null ? optsOrQuality : {};
  try {
    const u = new URL(url);
    // Only this project's published covers; all other remote URLs retain existing behavior.
    if (u.hostname === new URL(import.meta.env.VITE_SUPABASE_URL).hostname &&
        /^\/storage\/v1\/object\/public\/post-images\/covers\/[a-zA-Z0-9._-]+\.(webp|png|jpe?g|avif)$/.test(u.pathname)) {
      return u.pathname.replace("/storage/v1/object/public/post-images/", "/api/public/cover/");
    }
    const isWp = /(^|\.)wp\.com$/i.test(u.hostname);
    const isActnow = /(^|\.)actnow4planetearth\.com$/i.test(u.hostname);
    const isPexels = /(^|\.)pexels\.com$/i.test(u.hostname);
    if (isWp || isActnow || isPexels) {
      const qs = new URLSearchParams({ w: String(width) });
      if (opts.ar && Number.isFinite(opts.ar) && opts.ar > 0) {
        qs.set("ar", opts.ar.toFixed(4));
      }
      return `/api/public/img/${encodeURIComponent(url)}?${qs.toString()}`;
    }
    return url;
  } catch {
    return url;
  }
}

const STORAGE_OBJECT = /\/storage\/v1\/object\/public\//;

/**
 * Resized WebP variant of an image in our own public storage, via the storage
 * image renderer (center "cover" crop to the given ratio, same as the
 * object-cover box that displays it). Other hosts fall back to resizeImg.
 */
export function storageVariant(url: string | null | undefined, width: number, ar?: number): string {
  if (!url) return "";
  if (!STORAGE_OBJECT.test(url)) return resizeImg(url, width, ar ? { ar } : undefined);
  try {
    const u = new URL(url);
    u.pathname = u.pathname.replace(STORAGE_OBJECT, "/storage/v1/render/image/public/");
    u.search = "";
    u.searchParams.set("width", String(width));
    if (ar && ar > 0) {
      u.searchParams.set("height", String(Math.round(width / ar)));
      u.searchParams.set("resize", "cover");
    }
    u.searchParams.set("quality", "70");
    return u.toString();
  } catch {
    return url;
  }
}

/** srcset for a storage image at common reading widths (mobile → desktop). */
export function storageSrcSet(url: string | null | undefined, ar?: number, widths = [480, 800, 1200, 1600]): string | undefined {
  if (!url || !STORAGE_OBJECT.test(url)) return undefined;
  return widths.map((w) => `${storageVariant(url, w, ar)} ${w}w`).join(", ");
}
